import React, { useState, useEffect, useMemo } from 'react';
import { createUnregisteredTourReservation, getOrderSaleStatus, getSupplierCities } from '../services/apiService';
import { getUserInfo } from '../utils/tokenManager';
import { useNavigate } from 'react-router-dom';
import DateRangePicker from './DateRangePicker';

const phoneReg = /^\d{2,3}-?\d{3,4}-?\d{4}$/;

const calcDurationDays = (startStr, endStr) => {
  if (!startStr || !endStr) return '';
  try {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? String(diff) : '';
  } catch {
    return '';
  }
};

const NewReservationModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityCode, setCityCode] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [form, setForm] = useState({
    productName: '',
    durationDays: '',
    startDate: '',
    endDate: '',
    reserverName: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});

  const filteredCities = useMemo(() => {
    const q = (cityQuery || '').trim().toUpperCase();
    if (!q) return cityOptions;
    return cityOptions.filter((opt) =>
      String(opt.코드명 || '').toUpperCase().includes(q)
    );
  }, [cityQuery, cityOptions]);

  useEffect(() => {
    if (!open) return;
    setAnimatingOut(false);
    const id = requestAnimationFrame(() => setMounted(true));

    (async () => {
      try {
        setLoadingCities(true);
        const { companyNo } = getUserInfo() || {};
        if (!companyNo) return;
        const cities = await getSupplierCities({ companyNo }).catch(() => []);
        setCityOptions(Array.isArray(cities) ? cities : []);
      } catch (e) {
        console.error('도시 목록 로드 실패', e);
      } finally {
        setLoadingCities(false);
      }
    })();

    return () => cancelAnimationFrame(id);
  }, [open]);

  const resetForm = () => {
    setForm({
      productName: '',
      durationDays: '',
      startDate: '',
      endDate: '',
      reserverName: '',
      phone: '',
    });
    setCityCode('');
    setCityQuery('');
    setCityOpen(false);
    setErrors({});
  };

  const close = () => {
    setAnimatingOut(true);
    setMounted(false);
    window.setTimeout(() => {
      onClose && onClose();
      resetForm();
    }, 220);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const onStartDateChange = (date) => {
    setForm((prev) => {
      const end = prev.endDate || date;
      return {
        ...prev,
        startDate: date,
        durationDays: calcDurationDays(date, end) || prev.durationDays,
      };
    });
    if (errors.startDate) setErrors((prev) => ({ ...prev, startDate: '' }));
    if (errors.durationDays) setErrors((prev) => ({ ...prev, durationDays: '' }));
  };

  const onEndDateChange = (date) => {
    setForm((prev) => {
      const start = prev.startDate || date;
      return {
        ...prev,
        startDate: start,
        endDate: date,
        durationDays: calcDurationDays(start, date) || prev.durationDays,
      };
    });
    if (errors.startDate) setErrors((prev) => ({ ...prev, startDate: '' }));
    if (errors.durationDays) setErrors((prev) => ({ ...prev, durationDays: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.productName.trim()) e.productName = '상품명을 입력하세요.';
    if (!cityCode) e.cityCode = '대표도시를 선택하세요.';
    if (!String(form.durationDays).trim() || !/^\d+$/.test(String(form.durationDays))) e.durationDays = '소요일수(일)을 숫자로 입력하세요.';
    if (!form.startDate) e.startDate = '행사시작일을 선택하세요.';
    if (!form.reserverName.trim()) e.reserverName = '예약자를 입력하세요.';
    if (!form.phone.trim() || !phoneReg.test(form.phone.replace(/-/g, ''))) e.phone = '유효한 연락처를 입력하세요.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e && e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      const res = await createUnregisteredTourReservation({
        productName: form.productName,
        durationDays: Number(form.durationDays),
        startDate: form.startDate,
        reserverName: form.reserverName,
        phone: form.phone,
        cityNo: cityCode,
      });
      const row = Array.isArray(res?.table) ? res.table[0] : Array.isArray(res?.Table) ? res.Table[0] : null;
      const orderNo = row?.주문번호 ?? row?.orderNo ?? row?.OrderNo;
      let saleNo = row?.판매번호 ?? row?.saleNo ?? row?.주문판매번호;
      if (!orderNo) {
        alert('주문번호를 받지 못했습니다. 관리자에게 문의해주세요.');
        return;
      }
      try {
        if (!saleNo && orderNo) {
          const statusRow = await getOrderSaleStatus({ orderNo });
          saleNo = statusRow?.판매번호 ?? statusRow?.주문판매번호 ?? null;
        }
      } catch {}
      navigate(`/reservation-detail/${String(orderNo)}`, { state: { saleNo, productName: form.productName } });
      close();
    } catch (err) {
      console.error('신규예약 생성 실패', err);
      alert(err?.message || '신규예약 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const selectedCity = cityOptions.find((c) => String(c.코드) === String(cityCode));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={{ maxHeight: '90vh' }}
      >
        <div className="px-6 pt-5 pb-3 flex justify-between items-center">
          <h3 className="text-[17px] font-bold text-gray-900">신규예약 추가</h3>
          <button type="button" className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100" onClick={close}>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 130px)' }}>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">상품명</label>
              <input
                type="text"
                name="productName"
                value={form.productName}
                onChange={onChange}
                className={`w-full h-9 px-2.5 border ${errors.productName ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`}
                placeholder="상품명을 입력하세요"
              />
              {errors.productName && <p className="mt-1 text-sm text-red-600">{errors.productName}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">대표도시</label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full px-3 py-2 border rounded-lg text-left text-sm bg-white pr-10 ${
                    errors.cityCode ? 'border-red-500' : 'border-gray-300 hover:border-blue-300'
                  } transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  onClick={() => setCityOpen((o) => !o)}
                  disabled={loading || loadingCities}
                >
                  {loadingCities ? (
                    <span className="text-gray-400">도시 목록 불러오는 중...</span>
                  ) : selectedCity ? (
                    <span className="font-medium text-gray-900">{String(selectedCity.코드명)}</span>
                  ) : (
                    <span className="text-gray-400">대표도시를 선택하세요</span>
                  )}
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>
                {cityOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setCityOpen(false)} aria-hidden="true" />
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                      <div className="p-2 border-b bg-gray-50 rounded-t-lg">
                        <input
                          autoFocus
                          type="text"
                          value={cityQuery}
                          onChange={(e) => setCityQuery(e.target.value)}
                          placeholder="도시 검색"
                          className="w-full h-9 px-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <ul className="max-h-48 overflow-auto py-1">
                        {filteredCities.length === 0 && (
                          <li className="px-4 py-3 text-sm text-gray-500 text-center">검색 결과가 없습니다.</li>
                        )}
                        {filteredCities.map((opt) => (
                          <li
                            key={String(opt.코드)}
                            className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                              String(opt.코드) === String(cityCode)
                                ? 'bg-primary-50 text-primary-700 font-medium'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => {
                              setCityCode(String(opt.코드));
                              setCityOpen(false);
                              setCityQuery('');
                              if (errors.cityCode) setErrors((prev) => ({ ...prev, cityCode: '' }));
                            }}
                          >
                            {String(opt.코드명)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
              {errors.cityCode && <p className="mt-1 text-sm text-red-600">{errors.cityCode}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">행사시작일</label>
              <div className={errors.startDate ? 'rounded-lg ring-2 ring-red-500 ring-offset-1' : ''}>
                <DateRangePicker
                  startDate={form.startDate}
                  endDate={form.endDate}
                  onStartDateChange={onStartDateChange}
                  onEndDateChange={onEndDateChange}
                  singleCalendar
                  disabled={loading}
                />
              </div>
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">소요일수 (일)</label>
              <input
                type="number"
                name="durationDays"
                value={form.durationDays}
                onChange={onChange}
                className={`w-full h-9 px-2.5 border ${errors.durationDays ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`}
                min={1}
                placeholder="예: 5"
              />
              {errors.durationDays && <p className="mt-1 text-sm text-red-600">{errors.durationDays}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">예약자</label>
              <input
                type="text"
                name="reserverName"
                value={form.reserverName}
                onChange={onChange}
                className={`w-full h-9 px-2.5 border ${errors.reserverName ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`}
                placeholder="예약자 이름"
              />
              {errors.reserverName && <p className="mt-1 text-sm text-red-600">{errors.reserverName}</p>}
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-gray-600 mb-1">연락처</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={onChange}
                className={`w-full h-9 px-2.5 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500`}
                placeholder="010-0000-0000"
              />
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>
          </form>
        </div>

        <div className="px-6 pb-5 pt-3 flex justify-end gap-2">
          <button type="button" className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200" onClick={close}>
            취소
          </button>
          <button type="button" className="h-9 px-3.5 rounded-lg text-sm font-semibold bg-primary-600 text-white min-w-[100px]" onClick={onSubmit} disabled={loading || loadingCities}>
            {loading ? '처리중...' : '신규예약 생성'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewReservationModal;
